import { SecurityAudit } from "./SecurityAudit";
import { AccountRequests } from "./AccountRequests";
import { useEffect, useState } from "react";
import {
  roleIds,
  type RoleId,
  type Assignment,
  type AuthorizationSnapshot,
  type RoleAudit,
  type roleDefinitions,
} from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Button, Control } from "./components";
type Roster = {
  version: 1;
  stateRevision: number;
  assignments: Assignment[];
  people?: { id: string; name: string }[];
  current: AuthorizationSnapshot;
  roles: typeof roleDefinitions;
};
const messages: Record<string, string> = {
  TENANT_ROLE_ASSIGNMENT_STALE:
    "Access changed while you were editing. Review the refreshed roles before saving again.",
  TENANT_LAST_ADMIN_REQUIRED: "Keep at least one known tenant administrator.",
  TENANT_SELF_DEMOTION_CONFIRMATION_REQUIRED:
    "Confirm removing your administrator role before continuing.",
};
export function TenantAccess(props: { onSelfDemoted: () => void }) {
  const [tab, setTab] = useState("roles");
  return (
    <>
      <div className="access-tabs" role="tablist" aria-label="Tenant access">
        <button
          role="tab"
          aria-selected={tab === "roles"}
          onClick={() => setTab("roles")}
        >
          Roles
        </button>
        <button
          role="tab"
          aria-selected={tab === "requests"}
          onClick={() => setTab("requests")}
        >
          Account requests
        </button>
        <button
          role="tab"
          aria-selected={tab === "security"}
          onClick={() => setTab("security")}
        >
          Security audit
        </button>
      </div>
      {tab === "roles" ? (
        <RoleAdministration {...props} />
      ) : tab === "requests" ? (
        <AccountRequests />
      ) : (
        <SecurityAudit />
      )}
    </>
  );
}
function RoleAdministration({ onSelfDemoted }: { onSelfDemoted: () => void }) {
  const { api } = useWorkspaceTransport();
  const [roster, setRoster] = useState<Roster>(),
    [audits, setAudits] = useState<RoleAudit[]>([]),
    [subject, setSubject] = useState(""),
    [selected, setSelected] = useState<RoleId[]>(["tenant.tester"]),
    [confirm, setConfirm] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const refresh = async () => {
    const [next, history] = await Promise.all([
      api<Roster>("/tenant-access"),
      api<RoleAudit[]>("/tenant-access/audit"),
    ]);
    setRoster(next);
    setAudits(history);
    return next;
  };
  useEffect(() => {
    void refresh().catch((e) => setMessage(e.message));
  }, []);
  const edit = (id: string, roles: RoleId[]) => {
    setSubject(id);
    setSelected(roles);
    setConfirm(false);
    setMessage("");
  };
  const save = async () => {
    if (!roster) return;
    setBusy(true);
    setMessage("");
    try {
      await api("/tenant-access/roles", {
        method: "PUT",
        body: JSON.stringify({
          subjectId: subject.trim(),
          roleIds: selected,
          expectedAssignmentRevision:
            roster.assignments.find((a) => a.subjectId === subject.trim())
              ?.assignmentRevision || 0,
          expectedStateRevision: roster.stateRevision,
          confirmSelfDemotion: confirm,
        }),
      });
      if (
        subject.trim() === roster.current.subjectId &&
        !selected.includes("tenant.administrator")
      ) {
        onSelfDemoted();
        return;
      }
      await refresh();
      setMessage("Roles saved. Access refreshes on the next request.");
    } catch (e) {
      setMessage(messages[(e as Error).message] || (e as Error).message);
      try {
        const next = await refresh();
        const row = next.assignments.find(
          (a) => a.subjectId === subject.trim(),
        );
        if (row) setSelected(row.roleIds);
      } catch {
        /* A self-revocation may remove management access. */
      }
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };
  const known = roster
    ? [
        ...roster.assignments,
        ...(!roster.assignments.some(
          (a) => a.subjectId === roster.current.subjectId,
        )
          ? [roster.current]
          : []),
      ]
    : [];
  return (
    <section className="tenant-access">
      <span className="eyebrow">WORKSPACE</span>
      <h1>Users & roles</h1>
      <p>
        Manage access for host-authenticated subject IDs. Role changes never
        create login credentials.
      </p>
      {message && <p role="status">{message}</p>}
      <div className="access-editor">
        <Control label="Subject ID">
          <input
            value={subject}
            maxLength={200}
            onChange={(e) => {
              setSubject(e.target.value);
              setConfirm(false);
            }}
          />
        </Control>
        <fieldset>
          <legend>Assigned roles</legend>
          {(roster?.roles || []).map((role) => (
            <label key={role.id}>
              <input
                type="checkbox"
                checked={selected.includes(role.id)}
                onChange={(e) =>
                  setSelected(
                    roleIds.filter((id) =>
                      id === role.id ? e.target.checked : selected.includes(id),
                    ),
                  )
                }
              />
              {role.name}
            </label>
          ))}
        </fieldset>
        {roster?.current.subjectId === subject.trim() &&
          !selected.includes("tenant.administrator") && (
            <label>
              <input
                type="checkbox"
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
              />
              Confirm removing my administrator role
            </label>
          )}
        <p>
          {selected.length
            ? "Multiple roles grant their combined capabilities."
            : "An empty role set revokes membership and prevents host-role fallback."}
        </p>
        <Button
          kind="dark"
          disabled={busy || !roster || !subject.trim()}
          onClick={() => void save()}
        >
          Save roles
        </Button>
      </div>
      <h2>Known memberships</h2>
      <div className="access-memberships">
        {known.map((row) => (
          <article key={row.subjectId} data-access-subject={row.subjectId}>
            <div>
              <strong>
                {roster?.people?.find((person) => person.id === row.subjectId)
                  ?.name || row.subjectId}
              </strong>
              <small>{row.subjectId}</small>
              <p>{row.roleIds.length ? row.roleIds.join(", ") : "Revoked"}</p>
            </div>
            <button
              className="button"
              aria-label={`Edit ${row.subjectId}`}
              disabled={busy}
              onClick={() => edit(row.subjectId, row.roleIds)}
            >
              Edit roles
            </button>
          </article>
        ))}
      </div>
      <details>
        <summary>Role capabilities and boundaries</summary>
        {roster?.roles.map((role) => (
          <article key={role.id}>
            <h3>{role.name}</h3>
            <p>Allowed: {role.capabilities.join(", ")}</p>
            <p>
              Not granted:{" "}
              {role.nonCapabilities.join(", ") ||
                "All tenant capabilities are granted; platform access remains separate."}
            </p>
          </article>
        ))}
      </details>
      <h2>Role change audit</h2>
      <ol className="access-audit">
        {[...audits].reverse().map((row) => (
          <li key={row.id}>
            <strong>{row.targetSubjectId}</strong> · {row.action} ·{" "}
            {row.after.join(", ") || "Revoked"}
            <br />
            <small>
              {row.actorId} · assignment {row.assignmentRevision} ·{" "}
              {new Date(row.at).toLocaleString()}
            </small>
          </li>
        ))}
      </ol>
    </section>
  );
}
